import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useTranslation } from "react-i18next";
import { Tooltip, Typography } from "@mui/material";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { truncDynCell } from "common/Table/truncDynCell";

interface UploadPreviewTableProps {
  columns: any[];
  rows: any[];
  type?: string;
}

export const UploadPreviewTable: React.FC<UploadPreviewTableProps> = ({
  columns,
  rows,
  type,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows],
  );
  const getValueByPath = (obj: any, path: string) =>
    path.split(".").reduce((acc, part) => acc && acc[part], obj);
  return (
    <Box
      sx={{ width: "100%" }}
      className="reportsPreviewTable grid"
      id="inventorytable"
    >
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={"small"}
        >
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              {columns.map((column) => (
                <>
                  <TableCell
                    key={column.id}
                    align={column.numeric ? "right" : "left"}
                    padding={column.disablePadding ? "none" : "normal"}
                    sortDirection={false}
                  >
                    <span className="font-semibold">
                      {type === "invoices" && column.id === "invoiceItems" ? (
                        <>
                          {[
                            "category",
                            "title",
                            "code",
                            "quantity",
                            "tax",
                            "price",
                            "cost",
                          ].map((field) => (
                            <TableCell
                              key={field}
                              align={column.numeric ? "right" : "left"}
                              padding={
                                column.disablePadding ? "none" : "normal"
                              }
                              sortDirection={false}
                            >
                              <span className="font-semibold">{t(field)}</span>
                            </TableCell>
                          ))}
                        </>
                      ) : (
                        column.label
                      )}
                    </span>
                  </TableCell>
                </>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  className="py-3"
                >
                  <Typography
                    className="font-Saira font-[500] text-[15px] italic"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("noRecordFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, index) => {
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id ?? index}
                    sx={
                      Array.isArray(row.invalidValues) &&
                      row.invalidValues.length
                        ? { backgroundColor: "rgba(252, 95, 58, 0.12)" }
                        : undefined
                    }
                  >
                    <TableCell>{index + 1}</TableCell>
                    {columns.map((column, colIndex) => {
                      const error: boolean = (row.invalidValues as any)?.some(
                        (invalid: any) =>
                          invalid.Column === colIndex ||
                          invalid.column === colIndex,
                      );
                      const errorText =
                        (row.invalidValues as any)?.find(
                          (invalid: any) =>
                            invalid.Column === colIndex ||
                            invalid.column === colIndex,
                        )?.Error ||
                        (row.invalidValues as any)?.find(
                          (invalid: any) =>
                            invalid.Column === colIndex ||
                            invalid.column === colIndex,
                        )?.error;
                      const cellValue = getValueByPath(row, column.id);
                      return (
                        <Tooltip title={errorText || ""}>
                          <TableCell
                            key={column.id}
                            align={column.numeric ? "right" : "left"}
                            padding={column.disablePadding ? "none" : "normal"}
                            component={undefined}
                            id={undefined}
                            scope={undefined}
                            sx={{
                              width: "auto",
                              background: error ? "red" : "transparent",
                              color: error ? "#fff !important" : "inherit",
                            }}
                          >
                            {cellValue &&
                            typeof cellValue === "string" &&
                            /^\d{4}-\d{2}-\d{2}T/.test(cellValue as string) ? (
                              (cellValue as any)?.split("T")[0]
                            ) : column.id === "shippingMethod" ? (
                              [
                                { value: 1, label: t("Air") },
                                { value: 2, label: t("Sea") },
                                { value: 3, label: t("Ground") },
                              ].find(
                                (option: any) => option.value === cellValue,
                              )?.label
                            ) : column.id === "itemGroup" ? (
                              [
                                { value: 1, label: t("groups") },
                                { value: 2, label: t("open") },
                                { value: 3, label: t("selected") },
                                { value: 4, label: t("bundled") },
                              ].find(
                                (option: any) => option.value === cellValue,
                              )?.label
                            ) : column.id === "category" ? (
                              [
                                { value: 1, label: t("fixedAsset") },
                                { value: 2, label: t("inventoryItem") },
                                { value: 3, label: t("spotPurchaseItem") },
                              ].find(
                                (option: any) => option.value === cellValue,
                              )?.label
                            ) : column.id === "categoryService" ? (
                              [
                                { value: 1, label: t("internal") },
                                { value: 4, label: t("external") },
                              ].find(
                                (option: any) => option.value === row["type"],
                              )?.label
                            ) : column.id === "pricingScope" ? (
                              [
                                { value: 2, label: t("Part & Service") },
                                { value: 1, label: t("Service only") },
                              ].find(
                                (option: any) => option.value === cellValue,
                              )?.label
                            ) : column.id === "pricingType" ? (
                              [
                                { value: 1, label: t("fixedPrice") },
                                { value: 2, label: t("standardLaborCost") },
                              ].find(
                                (option: any) => option.value === cellValue,
                              )?.label
                            ) : column.id === "pricingTypePackage" ? (
                              [
                                { value: 1, label: t("fixed") },
                                { value: 2, label: t("serviceSum") },
                              ].find(
                                (option: any) =>
                                  option.value === row["pricingType"],
                              )?.label
                            ) : column.id === "account.companyType" ? (
                              [
                                { value: 1000, label: t("soleProprietor") },
                                { value: 1100, label: t("Corporate") },
                                { value: 1200, label: t("Government") },
                              ].find(
                                (option: any) =>
                                  option.value ===
                                  (row?.account as any)?.companyType,
                              )?.label
                            ) : column.id === "taxable" ||
                              column.id === "isTaxable" ||
                              column.id === "haveScrapItem" ||
                              column.id === "sellBelowCost" ||
                              column.id === "applyToAllStations" ||
                              column.id === "checklistMandatory" ? (
                              cellValue ? (
                                t("yes")
                              ) : (
                                t("no")
                              )
                            ) : type === "invoices" &&
                              column.id === "invoiceItems" ? (
                              <>
                                {[
                                  "category",
                                  "title",
                                  "code",
                                  "quantity",
                                  "tax",
                                  "price",
                                  "cost",
                                ].map((field) => (
                                  <TableCell key={field} align="left">
                                    {((row?.invoiceItems as any) || [])?.map(
                                      (item: any, idx: number) => (
                                        <Tooltip
                                          title={t(item[field]) || ""}
                                          placement="top"
                                        >
                                          <div
                                            key={idx}
                                            className="whitespace-nowrap text-ellipsis max-w-[60px] overflow-hidden text-center"
                                          >
                                            {t(item[field])}
                                          </div>
                                        </Tooltip>
                                      ),
                                    )}
                                  </TableCell>
                                ))}
                              </>
                            ) : (
                              truncDynCell(cellValue)
                            )}
                          </TableCell>
                        </Tooltip>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 20, 25, 50, 200]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className="hide-on-print"
        labelRowsPerPage={t("rowsPerPage")}
        showFirstButton
        showLastButton
      />
    </Box>
  );
};
